"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader, Calendar as CalendarIcon } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timeSlots = [{
  id: "09:00",
  label: "9:00 AM",
  available: true
}, {
  id: "10:00",
  label: "10:00 AM",
  available: true
}, {
  id: "11:00",
  label: "11:00 AM",
  available: false
}, {
  id: "13:00",
  label: "1:00 PM",
  available: true
}, {
  id: "14:00",
  label: "2:00 PM",
  available: true
}, {
  id: "15:00",
  label: "3:00 PM",
  available: true
}, {
  id: "16:00",
  label: "4:00 PM",
  available: true
}];
export default function ScheduleAConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="relative py-16 md:py-24 bg-gradient-to-br from-primary-light via-background to-accent overflow-hidden">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,hsl(230,90%,95%)_0%,transparent_50%),radial-gradient(circle_at_70%_80%,hsl(210,40%,93%)_0%,transparent_50%)]" />
      
      <div className="container relative z-10 mx-auto px-4 max-w-4xl">
        <div ref={ref} className="text-center mb-12 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(1rem)"
      }}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            Schedule a Consultation
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Book your personalized consultation with our real estate and digital marketing experts
          </p>
        </div>

        <FormProvider className="w-full" formId="consultation-booking-contact" sectionName="schedule-consultation" defaultValues={{
        name: "",
        email: "",
        phone: "",
        date: "",
        time: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        date: {
          required: true
        },
        time: {
          required: true
        }
      }} submitText="Confirm Booking" successMessage="Your consultation has been scheduled! We'll send you a confirmation email shortly.">
          <ConsultationForm isVisible={isVisible} />
        </FormProvider>
      </div>
    </section>;
}
function ConsultationForm({
  isVisible
}: {
  isVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setShowTimeSlots(!!date);
  };
  if (isSubmitted && !submitError) {
    return <Card className="bg-background/95 backdrop-blur-sm shadow-medium border border-border">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center">
              <SafeHydrate><CalendarIcon className="size-8 text-primary" /></SafeHydrate>
            </div>
            <p className="text-lg font-semibold text-foreground">{successMessage}</p>
            <Button onClick={resetForm} variant="outline" className="mt-2">
              Schedule Another Consultation
            </Button>
          </div>
        </CardContent>
      </Card>;
  }
  return <Card className="bg-background/95 backdrop-blur-sm shadow-medium border border-border transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(1rem)",
    transitionDelay: "100ms"
  }}>
      <CardHeader>
        <CardTitle>Select Date & Time</CardTitle>
        <CardDescription>Choose your preferred consultation slot</CardDescription>
      </CardHeader>
      <CardContent>
        <Form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            <div className="grid md:grid-cols-2 gap-6">
              <FormField control={control} name="name" rules={{
              required: "Name is required",
              minLength: {
                value: 2,
                message: "Min 2 characters"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your Full Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <FormField control={control} name="phone" rules={{
            required: "Phone is required",
            pattern: {
              value: /^\+?[0-9\s\-()]+$/,
              message: "Invalid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+234 800 123 4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="date" rules={{
            required: "Please select a date"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Select Date</FormLabel>
                  <FormControl>
                    <div className="flex justify-center">
                      <SafeHydrate><Calendar mode="single" selected={selectedDate} onSelect={date => {
                    handleDateSelect(date);
                    field.onChange(date?.toISOString() || "");
                  }} disabled={date => date < new Date()} className="rounded-md border border-border" /></SafeHydrate>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {showTimeSlots && <FormField control={control} name="time" rules={{
            required: "Please select a time slot"
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel>Select Time</FormLabel>
                    <FormControl>
                      <div className="grid gap-3">
                        <ToggleGroup type="single" value={selectedTime} onValueChange={value => {
                  setSelectedTime(value);
                  field.onChange(value);
                }} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 w-full">
                          {timeSlots.map((slot, i) => <ToggleGroupItem key={slot.id} value={slot.id} disabled={!slot.available} className="transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" style={{
                    opacity: showTimeSlots ? 1 : 0,
                    transform: showTimeSlots ? "translateY(0)" : "translateY(0.5rem)",
                    transitionDelay: `${i * 150}ms`
                  }} data-index={i}>
                              {slot.label}
                            </ToggleGroupItem>)}
                        </ToggleGroup>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />}

            {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-foreground-secondary hover:bg-secondary-hover hover:shadow-medium hover:scale-[1.03] active:bg-secondary-active active:scale-100 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-visible:ring-secondary/40">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Confirming...
                </> : "Confirm Booking"}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}